import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Button, Grid } from '@mui/material';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from 'utils/snackbar';

interface OwnerActionsProps {
  isOwner: boolean;
  isProductDeleted: boolean;
  isProductEraser: boolean;
  isDeleteDisabled: boolean;
  status: string;
  onEdit: () => void;
  onMarkSold: () => void;
  onVisibilityChange: () => void;
  onDelete: () => void;
}

export const OwnerActions = ({
  isOwner,
  isProductDeleted,
  isProductEraser,
  isDeleteDisabled,
  status,
  onEdit,
  onMarkSold,
  onVisibilityChange,
  onDelete,
}: OwnerActionsProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { showInfoNotification } = useNotification();

  const showDisabledNotification = () => {
    const message = `${t('components.page-product.product-details.delete-restriction')}`;

    showInfoNotification(message, { preventDuplicate: true });
  };

  if (!isOwner) {
    return null;
  }

  if (isProductDeleted) {
    return (
      <Alert severity="error" variant="filled" className="w-100 mb-1">
        {isProductEraser ? t('components.product-edit.deleted-by-owner') : t('components.product-edit.deleted')}
      </Alert>
    );
  }

  const renderDeleteButton = (deleteFn: () => void) => (
    <Button variant="outlined" fullWidth={true} onClick={deleteFn} startIcon={<DeleteForeverIcon />}>
      {t('common.delete')}
    </Button>
  );

  return (
    <Grid container className="owner-actions">
      {status !== 'sold' && (
        <>
          <Grid item xs={6} className="product-edit-btn-edit">
            <Button
              variant="outlined"
              color="primary"
              fullWidth={true}
              startIcon={<EditIcon />}
              onClick={onEdit}
              className="inline-flex-btn"
            >
              {t('components.page-product.product-details.edit')}
            </Button>
          </Grid>

          <Grid item xs={6} className="product-edit-btn-sold">
            <ConfirmationDialog
              title="components.page-product.product-details.confirmation.mark-as-sold-title"
              confirmText="components.page-product.product-details.confirmation.mark-as-sold-confirm"
              cancelText="components.page-product.product-details.confirmation.mark-as-sold-cancel"
            >
              <Button variant="outlined" color="primary" fullWidth={true} startIcon={<DoneIcon />} onClick={onMarkSold}>
                {t('components.page-product.product-details.mark-as-sold')}
              </Button>
            </ConfirmationDialog>
          </Grid>

          <Grid item xs={6} className="product-edit-btn-hidden">
            <Button
              variant="outlined"
              color="primary"
              fullWidth={true}
              startIcon={status !== 'not-visible' ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
              onClick={onVisibilityChange}
            >
              {status === 'not-visible'
                ? t('components.page-product.product-details.make-visible')
                : t('components.page-product.product-details.hide')}
            </Button>
          </Grid>
        </>
      )}

      <Grid item xs={status !== 'sold' ? 6 : 12} md={6} className="product-edit-btn-remove">
        {isDeleteDisabled ? (
          renderDeleteButton(showDisabledNotification)
        ) : (
          <ConfirmationDialog
            title="components.page-product.product-details.confirmation.delete-title"
            message="components.page-product.product-details.confirmation.delete-message"
          >
            {renderDeleteButton(onDelete)}
          </ConfirmationDialog>
        )}
      </Grid>
    </Grid>
  );
};

export default OwnerActions;
